import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { DropdownOption } from '../../shared/components/simple-dropdown/simple-dropdown.component';
import { SimpleAutocompleteComponent } from '../../shared/components/simple-autocomplete/simple-autocomplete.component';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { NotificationService } from '../../services/notification.service';
import { CompanyService } from '../services/company.service';
import { LocationSettingsService } from '../services/location-settings.service';
import { LaneService } from '../services/lane.service';
import { Lane } from '../models/lane.model';
import {
  GateEventRecord,
  StaggerGroup,
  groupStaggeredSequences,
  withTimeGaps,
} from './gate-event-grouping';
import { GateStreamHandle, GateStreamStatus, connectGateEventStream } from './gate-event-stream';

interface WatchedLane {
  laneId: string;
  laneName: string;
  gateSerial: string;
  /** Last-known status from /devices/status — informational only, never gates Connect. */
  reportedConnected: boolean | null;
  isCheckingStatus: boolean;
  streamStatus: GateStreamStatus | 'idle';
  handle: GateStreamHandle | null;
}

let eventIdCounter = 0;

/** Diagnostic session cap: oldest events drop once exceeded, so a long-running watch doesn't grow unbounded. */
const MAX_EVENTS = 500;

@Component({
  selector: 'app-gate-events',
  standalone: true,
  imports: [
    CommonModule,
    TitleBarComponent,
    SimpleAutocompleteComponent,
    ConfirmationModalComponent,
  ],
  templateUrl: './gate-events.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./gate-events.component.scss'],
})
export class GateEventsComponent implements OnInit, OnDestroy {
  private readonly companyService = inject(CompanyService);
  private readonly locationSettingsService = inject(LocationSettingsService);
  private readonly laneService = inject(LaneService);
  private readonly notificationService = inject(NotificationService);

  private readonly baseUrl = `${environment.baseUrl}/${environment.apiVersion}`;

  companyOptions: DropdownOption[] = [];
  selectedCompanyId = '';

  locationOptions: DropdownOption[] = [];
  selectedLocationId = '';

  loadingLanes = false;
  lanes: WatchedLane[] = [];

  events: GateEventRecord[] = [];

  confirmModalVisible = false;
  confirmModalData: ConfirmationModalData = {
    title: 'Connect to live device stream?',
    description: '',
    confirmText: 'Connect',
    cancelText: 'Cancel',
  };
  private pendingConnectLane: WatchedLane | null = null;

  /** Bumped on every selectCompany/loadLanes call so a slower, superseded request's
   * response is discarded instead of overwriting what the operator has since selected. */
  private locationRequestId = 0;
  private laneRequestId = 0;

  ngOnInit(): void {
    this.companyService.getCompanies({ limit: 1000, status: true }).subscribe({
      next: response => {
        const companies = Array.isArray(response.data) ? response.data : [];
        this.companyOptions = companies.map(company => ({
          label: company.name,
          value: company.id,
        }));
      },
      error: () => {
        this.notificationService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load car washes',
        });
      },
    });
  }

  ngOnDestroy(): void {
    for (const lane of this.lanes) {
      lane.handle?.disconnect();
    }
  }

  /** Chronological, interleaved — multi-lane groups render inline with a highlight, not split out. */
  get groupedEvents(): StaggerGroup[] {
    return groupStaggeredSequences(withTimeGaps(this.events));
  }

  selectCompany(companyId: string): void {
    // Invalidate any in-flight listLocations from a previous call, even when the new
    // selection clears the company entirely.
    const requestId = ++this.locationRequestId;
    this.selectedCompanyId = companyId || '';
    this.selectedLocationId = '';
    this.locationOptions = [];
    this.resetLanes();

    if (this.selectedCompanyId) {
      this.locationSettingsService.listLocations(this.selectedCompanyId).subscribe({
        next: response => {
          if (requestId !== this.locationRequestId) {
            return; // a newer selectCompany call has since superseded this one
          }
          const rows = Array.isArray(response.data) ? response.data : [];
          this.locationOptions = rows.map(row => ({
            label: row.name || row.branch || row.id,
            value: row.id,
          }));
        },
        error: () => {
          if (requestId !== this.locationRequestId) {
            return;
          }
          this.notificationService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load locations',
          });
        },
      });
    }
  }

  selectLocation(locationId: string): void {
    this.selectedLocationId = locationId || '';
    this.resetLanes();

    if (this.selectedCompanyId && this.selectedLocationId) {
      this.loadLanes();
    }
  }

  private loadLanes(): void {
    const requestId = ++this.laneRequestId;
    this.loadingLanes = true;
    this.laneService.listLanes(this.selectedCompanyId, this.selectedLocationId).subscribe({
      next: response => {
        if (requestId !== this.laneRequestId) {
          return; // a newer selectLocation call has since superseded this one
        }
        this.loadingLanes = false;
        const rows: Lane[] = Array.isArray(response.data) ? response.data : [];
        this.lanes = rows
          .filter(lane => !!lane.gate_serial)
          .map(lane => ({
            laneId: lane.id,
            laneName: lane.name,
            gateSerial: lane.gate_serial as string,
            reportedConnected: null,
            isCheckingStatus: true,
            streamStatus: 'idle',
            handle: null,
          }));
        for (const lane of this.lanes) {
          this.refreshLaneStatus(lane);
        }
      },
      error: () => {
        if (requestId !== this.laneRequestId) {
          return;
        }
        this.loadingLanes = false;
        this.notificationService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load lanes',
        });
      },
    });
  }

  private refreshLaneStatus(lane: WatchedLane): void {
    lane.isCheckingStatus = true;
    this.laneService.checkDeviceConnection(lane.gateSerial).subscribe({
      next: response => {
        lane.isCheckingStatus = false;
        lane.reportedConnected = Boolean(response.data?.connected);
      },
      error: () => {
        lane.isCheckingStatus = false;
        lane.reportedConnected = null;
      },
    });
  }

  onConnectClick(lane: WatchedLane): void {
    this.pendingConnectLane = lane;
    // Copy deliberately does not lean on reportedConnected — that status can be stale
    // (a device that dropped without closing cleanly still reports "connected"), so it's
    // shown as an unreliable hint elsewhere and never used to soften this warning.
    this.confirmModalData = {
      title: 'Connect to live device stream?',
      description: `Connecting to "${lane.laneName}" will immediately supersede any live device on this gate — no grace period. Its last-known status is not reliable enough to trust; only proceed if you're sure no physical gate is currently in service on this lane. Continue?`,
      confirmText: 'Connect',
      cancelText: 'Cancel',
    };
    this.confirmModalVisible = true;
  }

  onConfirmConnect(): void {
    const lane = this.pendingConnectLane;
    this.confirmModalVisible = false;
    this.pendingConnectLane = null;
    if (!lane) {
      return;
    }
    this.connectLane(lane);
  }

  onCancelConnect(): void {
    this.confirmModalVisible = false;
    this.pendingConnectLane = null;
  }

  private connectLane(lane: WatchedLane): void {
    let token: string | null = null;
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('superAdminData') : null;
      token = raw ? JSON.parse(raw)?.token || null : null;
    } catch {
      token = null;
    }
    if (!token) {
      this.notificationService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Super admin session not found — please re-login',
      });
      return;
    }

    lane.handle = connectGateEventStream(
      lane.gateSerial,
      this.baseUrl,
      token,
      frame => {
        const next = [
          ...this.events,
          {
            id: `evt-${++eventIdCounter}`,
            laneId: lane.laneId,
            laneName: lane.laneName,
            gateSerial: lane.gateSerial,
            ...frame,
          },
        ];
        // Cap the buffer so a long-running diagnostic session doesn't grow without bound.
        this.events = next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
      },
      status => {
        lane.streamStatus = status;
        if (status === 'closed' || status === 'error') {
          lane.handle = null;
        }
      }
    );
  }

  onDisconnectClick(lane: WatchedLane): void {
    lane.handle?.disconnect();
    lane.handle = null;
    lane.streamStatus = 'idle';
  }

  private resetLanes(): void {
    for (const lane of this.lanes) {
      lane.handle?.disconnect();
    }
    this.lanes = [];
    this.events = [];
  }
}
